import { useState, type ChangeEvent, type FC } from 'react';

import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { CheckmarkFilled, WarningAltFilled } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  CircularLoadingButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from '@mint-lib/tw-components';
import { TextArea } from '@mint-lib/tw-components/patterns';

import useSendApproverDecisionMutation from '../../api/mutations/useSendApproverDecisionMutation.jsx';

type Props = {
  variant: 'approve' | 'reject' | null;
  flowType: 'flight';
  processId: string | null;
  open: boolean;
  onClose: () => void;
  onDecisionSuccess: () => void;
};

const getNotificationMessageMapByVariant = (
  t: TFunction,
  entityType: string,
) => {
  return {
    approve: {
      success: t('This {{entityType}} has been approved.', {
        entityType,
      }),
      error: t('This {{entityType}} has not been approved.', {
        entityType,
      }),
    },
    reject: {
      success: t('This {{entityType}} has been rejected.', {
        entityType,
      }),
      error: t('This {{entityType}} has not been rejected.', {
        entityType,
      }),
    },
  };
};

const ApproverDecisionModal: FC<Props> = ({
  variant,
  flowType,
  processId,
  open,
  onClose,
  onDecisionSuccess,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');

  const [comment, setComment] = useState('');
  const { sendDecision, isPending } = useSendApproverDecisionMutation();
  const notificationMessageMapByVariant = getNotificationMessageMapByVariant(
    t,
    flowType,
  );

  const closeModal = () => {
    setComment('');
    onClose();
  };

  const handleModalOpenChange = (isOpened: boolean) => {
    if (isOpened) {
      return;
    }

    closeModal();
  };

  const handleChangeComment = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const handleSubmit = async () => {
    if (!variant || !processId) {
      return;
    }

    try {
      await sendDecision({ processId, status: variant, comment });
      onDecisionSuccess();
      enqueueSnackbar({
        variant: 'success',
        title: notificationMessageMapByVariant[variant].success,
        removable: true,
      });
      closeModal();
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: notificationMessageMapByVariant[variant].error,
        removable: true,
      });
    }
  };

  return (
    <Dialog modal onOpenChange={handleModalOpenChange} open={open}>
      <DialogPortal>
        <DialogOverlay />
        <DialogRoot
          size="m"
          className="outline-none"
          aria-describedby={undefined}
        >
          <DialogHeader className="gap-2">
            {variant === 'reject' && (
              <>
                <WarningAltFilled className="size-4 fill-error-main" />
                <DialogTitle>
                  {t('Reject this {{entityType}}?', { entityType: flowType })}
                </DialogTitle>
              </>
            )}
            {variant === 'approve' && (
              <>
                <CheckmarkFilled className="size-4 fill-success-main" />
                <DialogTitle>
                  {t('Approve this {{entityType}}?', { entityType: flowType })}
                </DialogTitle>
              </>
            )}
          </DialogHeader>
          <DialogContent>
            <div className="w-full">
              <p className="mb-4">
                {t('Add a comment if you want, then confirm your choice.')}
              </p>
              <TextArea
                data-dd-action-name="Comments input field"
                className="min-h-32"
                placeholder={t('Add a comment...')}
                value={comment}
                onChange={handleChangeComment}
              />
            </div>
          </DialogContent>
          <DialogFooter>
            <Button
              size="large"
              variant="text"
              type="button"
              onClick={closeModal}
              aria-label="Close the modal"
              data-dd-action-name="Close the modal"
            >
              {t('Cancel')}
            </Button>

            {variant === 'reject' && (
              <Button
                size="large"
                variant="contained"
                color="error"
                aria-label="Confirm rejection"
                data-dd-action-name="Submit action (reject)"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending && (
                  <CircularLoadingButton size="small" variant="contained" />
                )}
                {t('Yes, reject')}
              </Button>
            )}
            {variant === 'approve' && (
              <Button
                size="large"
                variant="contained"
                color="primary"
                aria-label="Confirm approval"
                data-dd-action-name="Submit action (approve"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending && (
                  <CircularLoadingButton size="small" variant="contained" />
                )}
                {t('Yes, approve')}
              </Button>
            )}
          </DialogFooter>
        </DialogRoot>
      </DialogPortal>
    </Dialog>
  );
};

export default ApproverDecisionModal;
